import { useEffect, useRef, useState } from "react";

/**
 * Hook that animates a numeric value when it changes.
 * Returns the animated value that smoothly transitions to the target.
 */
export function useAnimatedValue(
  targetValue: number,
  duration: number = 300
): number {
  const [displayValue, setDisplayValue] = useState(targetValue);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const startValueRef = useRef<number>(targetValue);

  useEffect(() => {
    // Cancel any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    // If values are the same (or very close), skip animation
    if (Math.abs(displayValue - targetValue) < 0.01) {
      setDisplayValue(targetValue);
      return;
    }

    startTimeRef.current = performance.now();
    startValueRef.current = displayValue;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);

      const newValue =
        startValueRef.current + (targetValue - startValueRef.current) * eased;
      setDisplayValue(newValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(targetValue);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetValue, duration]);

  return displayValue;
}

/**
 * Hook that returns true briefly when a value changes.
 * Useful for triggering highlight animations.
 */
export function useValueChanged(value: unknown, duration: number = 500): boolean {
  const [changed, setChanged] = useState(false);
  const prevRef = useRef(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (prevRef.current !== value) {
      prevRef.current = value;
      setChanged(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setChanged(false);
      }, duration);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, duration]);

  return changed;
}
